(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.QualityNdtAbcEmbed = api;
    const start = () => root.document.querySelectorAll('[data-abc-embed]').forEach(el => api.mount(root, el));
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
    root.document.addEventListener('shopify:section:load', start);
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  const CHANNEL = 'quality-ndt.abc-embed';
  const CHILD_ORIGIN = 'https://ab.quality-ndt.com';
  const PARENT_ORIGIN = 'https://quality-ndt.com';
  const LIMIT = 1000000;

  // Keep this bounded wire contract identical to EDU's child bridge.
  function validMessage(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
    const schemas = {
      hello: [], init: ['generation'], ready: ['generation'], navigation: ['generation'],
      height: ['generation', 'height'], scroll: ['generation', 'y'],
      viewport: ['generation', 'x', 'y', 'width', 'height', 'mode']
    };
    if (data.channel !== CHANNEL || data.version !== 1 || !Object.hasOwn(schemas, data.type)) return false;
    const fields = ['channel', 'version', 'type', ...schemas[data.type]];
    const keys = Object.keys(data);
    if (keys.length !== fields.length || keys.some(key => !fields.includes(key))) return false;
    if (data.type !== 'hello' && (typeof data.generation !== 'string' || !/^[a-f0-9]{64}$/.test(data.generation))) return false;
    if (data.type === 'height') return Number.isInteger(data.height) && data.height >= 1 && data.height <= LIMIT;
    if (data.type === 'scroll') return Number.isInteger(data.y) && data.y >= 0 && data.y <= LIMIT;
    if (data.type === 'viewport') return ['x', 'y', 'width', 'height'].every(key => Number.isFinite(data[key]) && data[key] >= 0 && data[key] <= LIMIT)
      && (data.mode === 'auto' || data.mode === 'fallback');
    return true;
  }

  function mount(w, section, options = {}) {
    if (section.__abcEmbed) return section.__abcEmbed;
    const frame = section.querySelector('[data-abc-frame]');
    const status = section.querySelector('[data-abc-status]');
    const seen = new Set();
    function diagnostic(code) {
      if (!seen.has(code)) { seen.add(code); w.console.warn('[abc-embed] stage=parent code=' + code); }
    }
    if (!frame || !status || w.location.origin !== PARENT_ORIGIN) {
      diagnostic('SETUP_INVALID');
      return null;
    }
    let generation = '', ready = false, mode = 'fallback', timer = 0, raf = 0;
    let pendingHeight = null, destroyed = false, suspended = false, navigationScroll = false;
    let lastInit = 0, lastViewport = '';
    const listeners = [];
    function listen(target, type, callback, opts) {
      target.addEventListener(type, callback, opts);
      listeners.push(() => target.removeEventListener(type, callback, opts));
    }
    function post(type, values) {
      if (!destroyed && !suspended && generation) frame.contentWindow.postMessage({ channel: CHANNEL, version: 1, type, generation, ...values }, CHILD_ORIGIN);
    }
    function bounds() {
      const visual = w.visualViewport;
      const left = visual ? visual.offsetLeft : 0;
      let top = visual ? visual.offsetTop : 0;
      const right = left + (visual ? visual.width : w.innerWidth);
      const bottom = top + (visual ? visual.height : w.innerHeight);
      const header = w.document.getElementById('header-component');
      if (header && w.document.fullscreenElement !== frame) {
        const box = header.getBoundingClientRect();
        // Use the actually visible header, including its sticky ancestor and transforms.
        if (box.top <= top + 1 && box.bottom > top) top = Math.min(bottom, box.bottom);
      }
      const box = frame.getBoundingClientRect();
      const x = Math.max(0, Math.min(box.width, left - box.left));
      const y = Math.max(0, Math.min(box.height, top - box.top));
      return { box, top, available: Math.max(0, bottom - top), x, y,
        width: Math.max(0, Math.min(box.right, right) - Math.max(box.left, left)),
        height: Math.max(0, Math.min(box.bottom, bottom) - Math.max(box.top, top)) };
    }
    function fallback(code) {
      mode = 'fallback';
      const height = Math.round(Math.max(320, Math.min(960, bounds().available)));
      frame.style.height = height + 'px';
      section.dataset.abcState = 'fallback';
      status.hidden = false;
      status.textContent = 'You can scroll inside the materials below or open ABC NDT in a separate tab.';
      if (code) diagnostic(code);
      queue();
    }
    function init() {
      // Coalesce repeated hello events without allowing them to extend the watchdog.
      const now = w.performance.now();
      if (lastInit && now - lastInit < 50) return;
      lastInit = now || 0.001;
      post('init', {});
    }
    function begin(sendInit, scrollAfterNavigation = false) {
      w.clearTimeout(timer);
      ready = false;
      pendingHeight = null;
      lastInit = 0;
      lastViewport = '';
      navigationScroll = scrollAfterNavigation;
      try {
        generation = Array.from(w.crypto.getRandomValues(new Uint8Array(32)), value => value.toString(16).padStart(2, '0')).join('');
      } catch (error) {
        generation = '';
        fallback('RANDOM_UNAVAILABLE');
        return;
      }
      section.dataset.abcState = 'loading';
      status.hidden = false;
      status.textContent = 'Loading NDT ABCs materials…';
      timer = w.setTimeout(() => fallback('READY_TIMEOUT'), options.watchdogMs || 10000);
      if (sendInit) init();
    }
    function scrollTo(y, afterKnownNavigation = false) {
      const view = bounds();
      if (view.width <= 0 || (!afterKnownNavigation && view.height <= 0) || w.document.activeElement !== frame) {
        diagnostic('SCROLL_INACTIVE');
        return;
      }
      const target = w.scrollY + view.box.top + Math.min(y, Math.max(0, view.box.height - 1)) - view.top;
      w.scrollTo({ top: Math.max(0, target), behavior: 'instant' });
    }
    function flush() {
      raf = 0;
      if (destroyed || suspended) return;
      if (pendingHeight !== null) {
        const height = pendingHeight;
        pendingHeight = null;
        if (height > 50000) fallback('HEIGHT_TOO_LARGE');
        else {
          mode = 'auto';
          frame.style.height = Math.max(320, height) + 'px';
          section.dataset.abcState = 'ready';
          status.hidden = true;
          status.textContent = '';
          w.clearTimeout(timer);
          if (navigationScroll) { navigationScroll = false; scrollTo(0, true); }
        }
      }
      if (ready) {
        const { x, y, width, height } = bounds();
        const values = { x, y, width, height, mode };
        if (!Object.values({ x, y, width, height }).every(value => Number.isFinite(value) && value >= 0 && value <= LIMIT)) {
          diagnostic('VIEWPORT_INVALID');
          return;
        }
        const next = JSON.stringify(values);
        if (lastViewport !== next) { lastViewport = next; post('viewport', values); }
      }
    }
    function queue() { if (!raf && !destroyed && !suspended) raf = w.requestAnimationFrame(flush); }
    function message(event) {
      if (destroyed || suspended || !event.data || event.data.channel !== CHANNEL) return;
      if (event.origin !== CHILD_ORIGIN || event.source !== frame.contentWindow) { diagnostic('SENDER_REJECTED'); return; }
      const data = event.data;
      if (!validMessage(data)) { diagnostic('MESSAGE_INVALID'); return; }
      if (data.type === 'hello') { if (!ready) init(); return; }
      if (data.generation !== generation) { diagnostic('GENERATION_STALE'); return; }
      if (data.type === 'ready') { ready = true; queue(); }
      else if (data.type === 'navigation' && ready) {
        const view = bounds();
        begin(false, view.width > 0 && view.height > 0 && w.document.activeElement === frame);
      }
      else if (data.type === 'height' && ready) { pendingHeight = data.height; queue(); }
      else if (data.type === 'scroll' && ready) scrollTo(data.y);
      else diagnostic('STATE_REJECTED');
    }
    listen(w, 'message', message);
    listen(frame, 'load', () => begin(true, navigationScroll));
    listen(frame, 'error', () => fallback('FRAME_EVENT_ERROR'));
    listen(w, 'resize', () => { if (mode === 'fallback') fallback(); queue(); }, { passive: true });
    listen(w, 'scroll', queue, { passive: true });
    listen(w.document, 'fullscreenchange', queue);
    if (w.visualViewport) {
      listen(w.visualViewport, 'resize', () => { if (mode === 'fallback') fallback(); queue(); }, { passive: true });
      listen(w.visualViewport, 'scroll', queue, { passive: true });
    }
    listen(w, 'pagehide', () => { suspended = true; w.clearTimeout(timer); w.cancelAnimationFrame(raf); raf = 0; });
    listen(w, 'pageshow', event => { if (event.persisted || suspended) { suspended = false; begin(true); } });
    const observer = w.ResizeObserver ? new w.ResizeObserver(queue) : null;
    if (observer) {
      observer.observe(frame);
      const header = w.document.getElementById('header-component');
      if (header) observer.observe(header);
    }
    const controller = { destroy() {
      if (destroyed) return;
      destroyed = true;
      w.clearTimeout(timer);
      w.cancelAnimationFrame(raf);
      if (observer) observer.disconnect();
      listeners.forEach(remove => remove());
      delete section.__abcEmbed;
    } };
    listen(w.document, 'shopify:section:unload', event => { if (event.target === section || event.target.contains(section)) controller.destroy(); });
    section.__abcEmbed = controller;
    fallback();
    begin(true);
    return controller;
  }
  return { validMessage, mount };
});
